import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import { Badge, Button, Card, EmptyState, ErrorState, LoadingState, PageHeader, TableScroll } from '../components/ui';
import { formatDate, formatExamCode } from '../utils/format';
import { getApiErrorMessage } from '../utils/examStart';
import './CohortPage.css';

/** 쿠폰 = 스터디 기수. 강사(지정된 쿠폰)와 관리자(전체)가 봅니다. */
interface CohortSummaryItem {
  coupon_code: string;
  name: string;
  exam_type_id: string;
  current_uses: number;
  max_uses: number;
  expires_at?: string;
  is_active: boolean;
}

interface CohortMember {
  user_id: string;
  name: string;
  email: string;
  joined_at: string;
  exam_attempts: number;
  practice_attempts: number;
  best_score: number | null;
  average_score: number | null;
  latest_score: number | null;
  latest_at: string | null;
  passed: boolean;
  inactive_days: number | null;
  weakest_domain: { name: string; rate: number } | null;
}

interface CohortDetail {
  coupon: { coupon_code: string; name: string; exam_type_id: string; expires_at?: string };
  passing_score: number;
  summary: { members: number; attempted: number; passed: number; average_best: number | null; inactive_7d: number };
  domains: Array<{ domain: string; name: string; average_rate: number | null }>;
  members: CohortMember[];
}

type MemberFilter = 'all' | 'attention' | 'not-attempted' | 'passed';

const INACTIVE_DAYS = 7;

const needsAttention = (member: CohortMember, passingScore: number) =>
  member.exam_attempts === 0 ||
  member.inactive_days === null ||
  member.inactive_days >= INACTIVE_DAYS ||
  (member.best_score !== null && member.best_score < passingScore - 10);

const CohortPage: React.FC = () => {
  const [cohorts, setCohorts] = useState<CohortSummaryItem[]>([]);
  const [selectedCode, setSelectedCode] = useState('');
  const [detail, setDetail] = useState<CohortDetail | null>(null);
  const [isLoadingList, setIsLoadingList] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [memberFilter, setMemberFilter] = useState<MemberFilter>('all');

  useEffect(() => {
    api.get<{ cohorts: CohortSummaryItem[] }>('/instructor/cohorts')
      .then((response) => {
        const list = response.data?.cohorts || [];
        setCohorts(list);
        const firstActive = list.find((item) => item.is_active) || list[0];
        if (firstActive) setSelectedCode(firstActive.coupon_code);
      })
      .catch((loadError) => setError(getApiErrorMessage(loadError) || '기수 목록을 불러오지 못했습니다.'))
      .finally(() => setIsLoadingList(false));
  }, []);

  useEffect(() => {
    if (!selectedCode) return;
    setIsLoadingDetail(true);
    setDetail(null);
    setMemberFilter('all');
    api.get<CohortDetail>(`/instructor/cohorts/${encodeURIComponent(selectedCode)}`)
      .then((response) => setDetail(response.data || null))
      .catch((loadError) => setError(getApiErrorMessage(loadError) || '기수 현황을 불러오지 못했습니다.'))
      .finally(() => setIsLoadingDetail(false));
  }, [selectedCode]);

  const members = useMemo(() => {
    if (!detail) return [];
    return detail.members.filter((member) => {
      if (memberFilter === 'attention') return needsAttention(member, detail.passing_score);
      if (memberFilter === 'not-attempted') return member.exam_attempts === 0;
      if (memberFilter === 'passed') return member.passed;
      return true;
    });
  }, [detail, memberFilter]);

  const attentionCount = detail ? detail.members.filter((member) => needsAttention(member, detail.passing_score)).length : 0;
  const weakestCohortDomain = detail
    ? [...detail.domains].filter((item) => item.average_rate !== null).sort((left, right) => (left.average_rate ?? 0) - (right.average_rate ?? 0))[0]
    : undefined;

  if (isLoadingList) return <LoadingState label="기수 목록을 불러오는 중..." />;

  if (error && cohorts.length === 0) {
    return <ErrorState title="기수 정보를 불러오지 못했습니다" description={error} />;
  }

  return (
    <div className="cohort-page">
      <PageHeader
        eyebrow="강의"
        title="스터디 기수 현황"
        description="쿠폰으로 들어온 수강생의 응시 현황, 점수, 약한 영역을 확인합니다."
        actions={cohorts.length > 0 && (
          <select
            className="cohort-page__select"
            value={selectedCode}
            onChange={(event) => setSelectedCode(event.target.value)}
            aria-label="기수 선택"
          >
            {cohorts.map((cohort) => (
              <option key={cohort.coupon_code} value={cohort.coupon_code}>
                {cohort.name || cohort.coupon_code} · {formatExamCode(cohort.exam_type_id)}{cohort.is_active ? '' : ' (종료)'}
              </option>
            ))}
          </select>
        )}
      />

      {cohorts.length === 0 ? (
        <Card padding="none" tone="major">
          <EmptyState
            title="볼 수 있는 기수가 없습니다"
            description="관리자에게 담당 쿠폰(기수) 지정을 요청하세요."
          />
        </Card>
      ) : isLoadingDetail || !detail ? (
        error ? <ErrorState title="기수 현황을 불러오지 못했습니다" description={error} /> : <LoadingState label="수강생 현황을 모으는 중..." />
      ) : (
        <>
          <section className="cohort-page__stats" aria-label="기수 요약">
            <Card padding="md">
              <span className="cohort-page__stat-label">수강생</span>
              <strong className="cohort-page__stat-value tabular">{detail.summary.members}</strong>
              <span className="cohort-page__stat-meta">실전 응시 {detail.summary.attempted}명</span>
            </Card>
            <Card padding="md">
              <span className="cohort-page__stat-label">합격선 도달</span>
              <strong className="cohort-page__stat-value tabular">{detail.summary.passed}</strong>
              <span className="cohort-page__stat-meta">합격선 {detail.passing_score}%</span>
            </Card>
            <Card padding="md">
              <span className="cohort-page__stat-label">평균 최고점</span>
              <strong className="cohort-page__stat-value tabular">{detail.summary.average_best ?? '-'}{detail.summary.average_best !== null && '%'}</strong>
              <span className="cohort-page__stat-meta">응시자 기준</span>
            </Card>
            <Card padding="md">
              <span className="cohort-page__stat-label">관리 필요</span>
              <strong className="cohort-page__stat-value cohort-page__stat-value--alert tabular">{attentionCount}</strong>
              <span className="cohort-page__stat-meta">미응시·{INACTIVE_DAYS}일 미활동·합격선 -10%p 미만</span>
            </Card>
          </section>

          {detail.domains.some((item) => item.average_rate !== null) && (
            <Card padding="md" title="영역별 평균 정답률" subtitle={weakestCohortDomain ? `가장 약한 영역: ${weakestCohortDomain.name}` : undefined}>
              <ul className="cohort-page__domains">
                {detail.domains.map((item) => (
                  <li key={item.domain}>
                    <span className="cohort-page__domain-name">{item.name}</span>
                    <span className="cohort-page__domain-bar" aria-hidden="true">
                      <span
                        className={(item.average_rate ?? 0) >= detail.passing_score ? 'is-pass' : 'is-fail'}
                        style={{ width: `${item.average_rate ?? 0}%` }}
                      />
                    </span>
                    <span className="cohort-page__domain-rate tabular">{item.average_rate === null ? '데이터 없음' : `${item.average_rate}%`}</span>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          <Card padding="none" className="cohort-page__members">
            <div className="cohort-page__members-head">
              <h2>수강생 {members.length}명</h2>
              <div className="cohort-page__filters" role="group" aria-label="수강생 필터">
                {([
                  ['all', '전체'],
                  ['attention', `관리 필요 ${attentionCount}`],
                  ['not-attempted', '미응시'],
                  ['passed', '합격선 도달'],
                ] as Array<[MemberFilter, string]>).map(([value, label]) => (
                  <Button
                    key={value}
                    size="sm"
                    variant={memberFilter === value ? 'primary' : 'secondary'}
                    aria-pressed={memberFilter === value}
                    onClick={() => setMemberFilter(value)}
                  >
                    {label}
                  </Button>
                ))}
              </div>
            </div>

            {members.length === 0 ? (
              <EmptyState title="해당하는 수강생이 없습니다" />
            ) : (
              <TableScroll>
                <table className="cohort-page__table">
                  <thead>
                    <tr>
                      <th>수강생</th>
                      <th className="is-num">실전 응시</th>
                      <th className="is-num">최고</th>
                      <th className="is-num">평균</th>
                      <th className="is-num">최근</th>
                      <th>마지막 활동</th>
                      <th>약한 영역</th>
                      <th>상태</th>
                    </tr>
                  </thead>
                  <tbody>
                    {members.map((member) => (
                      <tr key={member.user_id}>
                        <td>
                          <span className="cohort-page__member-name">{member.name}</span>
                          <span className="cohort-page__member-email">{member.email}</span>
                        </td>
                        <td className="is-num tabular">
                          {member.exam_attempts}
                          {member.practice_attempts > 0 && <span className="cohort-page__muted"> +연습 {member.practice_attempts}</span>}
                        </td>
                        <td className="is-num tabular">{member.best_score ?? '-'}</td>
                        <td className="is-num tabular">{member.average_score ?? '-'}</td>
                        <td className="is-num tabular">{member.latest_score ?? '-'}</td>
                        <td>
                          {member.latest_at ? formatDate(member.latest_at) : '-'}
                          {member.inactive_days !== null && member.inactive_days >= INACTIVE_DAYS && (
                            <span className="cohort-page__muted"> ({member.inactive_days}일 전)</span>
                          )}
                        </td>
                        <td>{member.weakest_domain ? `${member.weakest_domain.name} ${member.weakest_domain.rate}%` : '-'}</td>
                        <td>
                          {member.passed ? (
                            <Badge tone="success">합격선 도달</Badge>
                          ) : member.exam_attempts === 0 ? (
                            <Badge tone="warning">미응시</Badge>
                          ) : needsAttention(member, detail.passing_score) ? (
                            <Badge tone="danger">관리 필요</Badge>
                          ) : (
                            <Badge tone="info">진행 중</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableScroll>
            )}
          </Card>
        </>
      )}
    </div>
  );
};

export default CohortPage;
